/**
 * Issues slice - single source for issues API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { ISSUES } from '../../api/endpoints';

export const getIssues = createAsyncThunk(
  'issues/getIssues',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(ISSUES.LIST, { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch issues');
    }
  }
);

export const getIssueById = createAsyncThunk(
  'issues/getIssueById',
  async (issueId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/issues/${issueId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch issue details');
    }
  }
);

export const createIssue = createAsyncThunk(
  'issues/createIssue',
  async (issueData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(ISSUES.CREATE, issueData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create issue');
    }
  }
);

export const updateIssueStatus = createAsyncThunk(
  'issues/updateIssueStatus',
  async ({ issueId, statusData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/issues/${issueId}/update-status`, statusData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update issue status');
    }
  }
);

export const assignIssueToUser = createAsyncThunk(
  'issues/assignIssueToUser',
  async ({ issueId, userId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(ISSUES.ASSIGN_USER(issueId), {
        assignee_id: userId,
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to assign issue');
    }
  }
);

export const deleteIssue = createAsyncThunk(
  'issues/deleteIssue',
  async (issueId, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/issues/${issueId}`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete issue');
    }
  }
);

export const getIssueAssignedUsers = createAsyncThunk(
  'issues/getIssueAssignedUsers',
  async (issueId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(ISSUES.ASSIGNED_USERS(issueId));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch assigned users');
    }
  }
);

const initialState = {
  issues: null,
  currentIssue: null,
  assignedUsers: null,
  statuses: null,
  loading: false,
  error: null,
};

const issueSlice = createSlice({
  name: 'issues',
  initialState,
  reducers: {
    clearCurrentIssue: (state) => {
      state.currentIssue = null;
    },
    clearIssuesError: (state) => {
      state.error = null;
    },
    setIssuesList: (state, { payload }) => {
      const updater = payload;
      const list = state.issues?.data?.list ?? state.issues?.list;
      if (Array.isArray(list) && typeof updater === 'function') {
        const newList = updater(list);
        if (state.issues?.data) state.issues.data.list = newList;
        else if (state.issues) state.issues.list = newList;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getIssues.fulfilled, (state, action) => {
        state.loading = false;
        state.issues = action.payload;
      })
      .addCase(getIssueById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentIssue = action.payload;
      })
      .addCase(getIssueAssignedUsers.fulfilled, (state, action) => {
        state.assignedUsers = action.payload;
      })
      .addMatcher(
        (a) =>
          [
            getIssues.pending,
            getIssueById.pending,
            createIssue.pending,
            updateIssueStatus.pending,
            assignIssueToUser.pending,
            deleteIssue.pending,
          ].some((t) => t.match(a)),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          [
            getIssues.rejected,
            getIssueById.rejected,
            createIssue.rejected,
            updateIssueStatus.rejected,
            assignIssueToUser.rejected,
            deleteIssue.rejected,
          ].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      )
      .addMatcher(
        (a) =>
          [
            createIssue.fulfilled,
            updateIssueStatus.fulfilled,
            assignIssueToUser.fulfilled,
            deleteIssue.fulfilled,
          ].some((t) => t.match(a)),
        (state) => {
          state.loading = false;
        }
      );
  },
});

export const { clearCurrentIssue, clearIssuesError, setIssuesList } = issueSlice.actions;
export default issueSlice.reducer;
